import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Car, Download, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { exportVehicleLogs, getVehicleLogs, saveVehicleLog } from '../../services/api';
import { businessNow, toDateKey } from '../../lib/timeclock';

const toInt = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits === '' ? null : Number(digits);
};
const formatKm = (value) => (value === null || value === undefined ? '-' : value.toLocaleString('fr-CA'));
const detailOf = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};
const firstOfMonth = (key) => `${key.slice(0, 8)}01`;

// Registre quotidien du véhicule : odomètre de début et de fin de journée.
export const VehicleLogCard = ({ userId, canRecord, teamView, people = [] }) => {
  const today = toDateKey(businessNow());
  const [date, setDate] = useState(today);
  const [form, setForm] = useState({ vehicle: '', odometer_start: '', odometer_end: '' });
  const [logs, setLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [range, setRange] = useState({ from: firstOfMonth(today), to: today });
  const [employeeFilter, setEmployeeFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const params = { date_from: range.from, date_to: range.to };
      if (teamView && employeeFilter) params.employee_id = employeeFilter;
      const { data } = await getVehicleLogs(params);
      setLogs(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Erreur lors du chargement du kilométrage');
    }
  }, [range.from, range.to, teamView, employeeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const mine = useMemo(() => logs.filter((log) => log.employee_id === userId), [logs, userId]);

  // Préremplit la journée choisie; le véhicule reprend celui de la dernière journée.
  useEffect(() => {
    const current = mine.find((log) => log.date === date);
    const last = mine.find((log) => log.vehicle);
    setForm({
      vehicle: current?.vehicle || last?.vehicle || '',
      odometer_start: current?.odometer_start ?? '',
      odometer_end: current?.odometer_end ?? '',
    });
  }, [date, mine]);

  const start = toInt(form.odometer_start);
  const end = toInt(form.odometer_end);
  const distance = start !== null && end !== null ? end - start : null;
  const invalid = distance !== null && distance < 0;

  const handleSave = async (event) => {
    event.preventDefault();
    if (invalid) {
      toast.error('Le kilométrage de fin doit être supérieur ou égal à celui du début');
      return;
    }
    if (start === null && end === null) {
      toast.error('Indiquez au moins le kilométrage de début');
      return;
    }
    setSaving(true);
    try {
      const payload = { vehicle: form.vehicle.trim() || null };
      if (start !== null) payload.odometer_start = start;
      if (end !== null) payload.odometer_end = end;
      const { data } = await saveVehicleLog(date, payload);
      toast.success(data.distance !== null ? `Kilométrage enregistré : ${formatKm(data.distance)} km` : 'Kilométrage de début enregistré');
      load();
    } catch (error) {
      toast.error(detailOf(error, "Erreur lors de l'enregistrement"));
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = { date_from: range.from, date_to: range.to };
      if (teamView && employeeFilter) params.employee_id = employeeFilter;
      const { data } = await exportVehicleLogs(params);
      const url = URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `kilometrage_${range.from}_${range.to}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const totalKm = logs.reduce((sum, log) => sum + (log.distance || 0), 0);

  return (
    <Card data-testid="vehicle-log-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Car className="w-5 h-5 text-[#00b4d8]" />
          Kilométrage du véhicule
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {canRecord && (
          <form onSubmit={handleSave} className="space-y-4" data-testid="vehicle-log-form">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 items-end">
              <div>
                <Label htmlFor="vehicle-date">Date</Label>
                <Input id="vehicle-date" type="date" value={date} max={today} onChange={(e) => setDate(e.target.value || today)} />
              </div>
              <div>
                <Label htmlFor="vehicle-name">Véhicule</Label>
                <Input id="vehicle-name" value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })}
                  maxLength={80} placeholder="Ex. : Camion 2" />
              </div>
              <div>
                <Label htmlFor="odometer-start">Début (km)</Label>
                <Input id="odometer-start" inputMode="numeric" value={form.odometer_start}
                  onChange={(e) => setForm({ ...form, odometer_start: e.target.value.replace(/\D/g, '') })}
                  placeholder="152340" data-testid="odometer-start" />
              </div>
              <div>
                <Label htmlFor="odometer-end">Fin (km)</Label>
                <Input id="odometer-end" inputMode="numeric" value={form.odometer_end}
                  onChange={(e) => setForm({ ...form, odometer_end: e.target.value.replace(/\D/g, '') })}
                  placeholder="152478" className={invalid ? 'border-red-400' : ''} data-testid="odometer-end" />
              </div>
              <div className="col-span-2 lg:col-span-1 flex items-center justify-between lg:justify-end gap-3">
                <span className={`text-sm font-semibold ${invalid ? 'text-red-600' : 'text-[#1a3a5c]'}`} data-testid="vehicle-distance">
                  {distance === null ? '' : `${formatKm(distance)} km`}
                </span>
                <Button type="submit" disabled={saving} className="bg-[#00b4d8] hover:bg-[#009bb8]" data-testid="vehicle-save">
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </Button>
              </div>
            </div>
            <p className="text-xs text-slate-500">Inscrivez l'odomètre au départ le matin et à l'arrivée le soir.</p>
          </form>
        )}

        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label htmlFor="range-from" className="text-xs">Du</Label>
              <Input id="range-from" type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="h-9" />
            </div>
            <div>
              <Label htmlFor="range-to" className="text-xs">Au</Label>
              <Input id="range-to" type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="h-9" />
            </div>
            {teamView && people.length > 1 && (
              <div>
                <Label htmlFor="range-employee" className="text-xs">Employé</Label>
                <select
                  id="range-employee"
                  value={employeeFilter}
                  onChange={(e) => setEmployeeFilter(e.target.value)}
                  className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
                >
                  <option value="">Tous</option>
                  {people.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
                </select>
              </div>
            )}
            <span className="text-sm text-slate-600 sm:ml-auto">Total : <strong>{formatKm(totalKm)} km</strong></span>
            <Button type="button" variant="outline" size="sm" onClick={handleExport} data-testid="vehicle-export">
              <Download className="w-4 h-4 mr-2" />
              Exporter (Excel)
            </Button>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {teamView && <TableHead>Employé</TableHead>}
                  <TableHead>Date</TableHead>
                  <TableHead>Véhicule</TableHead>
                  <TableHead className="text-right">Début</TableHead>
                  <TableHead className="text-right">Fin</TableHead>
                  <TableHead className="text-right">Km</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id || `${log.employee_id}-${log.date}`}>
                    {teamView && <TableCell>{log.employee_name}</TableCell>}
                    <TableCell className="whitespace-nowrap">{log.date}</TableCell>
                    <TableCell>{log.vehicle || '-'}</TableCell>
                    <TableCell className="text-right">{formatKm(log.odometer_start)}</TableCell>
                    <TableCell className="text-right">
                      {log.odometer_end === null || log.odometer_end === undefined
                        ? <span className="text-amber-700">à inscrire</span>
                        : formatKm(log.odometer_end)}
                    </TableCell>
                    <TableCell className="text-right font-medium">{formatKm(log.distance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {logs.length === 0 && <p className="text-center text-sm text-slate-500 py-6">Aucun kilométrage pour cette période</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default VehicleLogCard;
